import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import SharedPageLayout from '../components/SharedPageLayout';
import Footer from '../components/Footer';
import MainNavigation from '../components/MainNavigation';
import ThreeDPreview from '../components/ThreeDPreview';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Eye, Edit, X, CheckCircle, Package, User, Upload, ShoppingCart } from 'lucide-react';
import { trackDesignView } from '../utils/analytics';
import { BACKEND_URL } from '../config';

const API = `${BACKEND_URL}/api`;


export const AdminMarketplaceRoute = () => {
  const navigate = useNavigate();
  const { user, getIdToken, testLogin } = useAuth();
  const [applications, setApplications] = useState([]);
  const [listings, setListings] = useState([]);
  const [activeTab, setActiveTab] = useState('applications');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      try {
        // Use proper backend URL from environment variable
        const API_BASE = BACKEND_URL;
        const token = await getIdToken();

        // Fetch designer applications
        const appsResponse = await fetch(`${API_BASE}/api/marketplace/applications`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (appsResponse.ok) {
          const appsResult = await appsResponse.json();
          setApplications(appsResult.applications || []);
        }

        // Fetch all listings
        const listingsResponse = await fetch(`${API_BASE}/api/marketplace/listings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (listingsResponse.ok) {
          const listingsResult = await listingsResponse.json();
          setListings(listingsResult.listings || []);
        }

      } catch (error) {
        console.error('Admin data fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const reviewApplication = async (applicationId, status, adminNotes = '') => {
    try {
      // Use proper backend URL from environment variable
      const API_BASE = BACKEND_URL;
      const token = await getIdToken();

      const response = await fetch(`${API_BASE}/api/marketplace/applications/${applicationId}?status=${status}&admin_notes=${encodeURIComponent(adminNotes)}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        // Refresh applications
        const appsResponse = await fetch(`${API_BASE}/api/marketplace/applications`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (appsResponse.ok) {
          const appsResult = await appsResponse.json();
          setApplications(appsResult.applications || []);
        }
      }
    } catch (error) {
      console.error('Review application error:', error);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="text-center p-6">
            <h2 className="text-xl font-semibold mb-4">Admin Access Required</h2>
            <p className="text-gray-600 mb-4">Please sign in with admin credentials.</p>
            <div className="space-y-3">
              <Button onClick={() => navigate('/')}>Go to Home</Button>
              {/* Temporary Test Admin Access - Development Only */}
              <Button 
                onClick={testLogin}
                className="bg-orange-600 hover:bg-orange-700 w-full"
              >
                🔧 Test Admin Access (Dev Only)
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center">
                <Settings className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Marketplace Admin</h1>
            </div>
            <Button onClick={() => navigate('/admin')} variant="outline">
              Back to Admin
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="applications">Designer Applications</TabsTrigger>
            <TabsTrigger value="listings">Marketplace Listings</TabsTrigger>
          </TabsList>

          <TabsContent value="applications" className="mt-8">
            <Card>
              <CardHeader>
                <CardTitle>Designer Applications</CardTitle>
                <CardDescription>
                  Review and approve designer applications to join the marketplace.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0007b5] mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading applications...</p>
                  </div>
                ) : applications.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600">No applications found.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {applications.map((app) => (
                      <Card key={app.id} className="border-l-4 border-l-blue-500">
                        <CardContent className="p-6">
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div className="lg:col-span-2">
                              <div className="flex items-start justify-between mb-4">
                                <div>
                                  <h3 className="text-lg font-semibold text-gray-900">{app.designer_name}</h3>
                                  <p className="text-gray-600">{app.user_email}</p>
                                </div>
                                <Badge 
                                  variant={app.status === 'pending' ? 'secondary' : 
                                          app.status === 'approved' ? 'default' : 'destructive'}
                                >
                                  {app.status}
                                </Badge>
                              </div>
                              
                              <div className="space-y-3">
                                <div>
                                  <Label className="font-medium text-gray-700">Bio</Label>
                                  <p className="text-gray-600 text-sm mt-1">{app.bio}</p>
                                </div>
                                
                                {app.portfolio_links.length > 0 && (
                                  <div>
                                    <Label className="font-medium text-gray-700">Portfolio Links</Label>
                                    <div className="mt-1 space-y-1">
                                      {app.portfolio_links.map((link, index) => (
                                        <a 
                                          key={index} 
                                          href={link} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="text-[#0007b5] hover:underline text-sm block"
                                        >
                                          {link}
                                        </a>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {app.status === 'pending' && (
                              <div className="space-y-3">
                                <div>
                                  <Label className="font-medium text-gray-700">Admin Actions</Label>
                                  <div className="mt-2 space-y-2">
                                    <Button 
                                      onClick={() => reviewApplication(app.id, 'approved', 'Application approved - welcome to the marketplace!')}
                                      className="w-full bg-green-600 hover:bg-green-700"
                                      size="sm"
                                    >
                                      Approve Application
                                    </Button>
                                    <Button 
                                      onClick={() => reviewApplication(app.id, 'rejected', 'Application does not meet current requirements')}
                                      variant="destructive"
                                      className="w-full"
                                      size="sm"
                                    >
                                      Reject Application
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <div className="mt-4 pt-4 border-t text-xs text-gray-500">
                            Applied: {new Date(app.created_at).toLocaleDateString()}
                            {app.reviewed_at && (
                              <span className="ml-4">
                                Reviewed: {new Date(app.reviewed_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="listings" className="mt-8">
            <Card>
              <CardHeader>
                <CardTitle>Marketplace Listings</CardTitle>
                <CardDescription>
                  Manage and moderate marketplace listings.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {listings.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600">No listings found.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {listings.map((listing) => (
                      <Card key={listing.id} className="hover:shadow-lg transition-shadow">
                        <CardContent className="p-4">
                          <div className="aspect-square bg-gray-100 rounded-lg mb-3 flex items-center justify-center">
                            <Package className="w-12 h-12 text-gray-400" />
                          </div>
                          <h3 className="font-semibold text-gray-900 mb-2">{listing.title}</h3>
                          <p className="text-sm text-gray-600 mb-2">
                            by{' '}
                            {listing.creator_username ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/creator/${listing.creator_username}`);
                                }}
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {listing.designer_display_name || listing.designer_name}
                              </button>
                            ) : (
                              <span>{listing.designer_display_name || listing.designer_name}</span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500 mb-3 line-clamp-2">{listing.description}</p>
                          
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span>Personal:</span>
                              <span className="font-semibold">${listing.personal_price}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Commercial:</span>
                              <span className="font-semibold">${listing.commercial_price}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Downloads:</span>
                              <span>{listing.download_count}</span>
                            </div>
                          </div>
                          
                          <div className="mt-3 pt-3 border-t flex items-center justify-between">
                            <Badge 
                              variant={listing.status === 'active' ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              {listing.status}
                            </Badge>
                            <span className="text-xs text-gray-500">{listing.category}</span>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};


export const MarketplaceRoute = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatPrice } = useCurrency();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: '',
    use_case: '',
    listing_type: '',
    pricing_tier: '',
    search: '',
    max_price: ''
  });

  const categories = [
    'Automotive', 'Home & Living', 'Garden & Outdoor', 'Organization', 
    'Electronics', 'Toys & Games', 'Art & Decorative', 'Tools & Hardware',
    'Fashion & Jewelry', 'Replacement Parts', 'Sim Racing', 'Flight Simulator', 'Other'
  ];

  const useCases = ['Functional', 'Decorative', 'Replacement', 'Educational', 'Prototype'];

  useEffect(() => {
    const fetchListings = async () => {
      setLoading(true);
      try {
        // Use proper backend URL from environment variable
        const API_BASE = BACKEND_URL;
        const params = new URLSearchParams();
        
        if (filters.category && filters.category !== 'all-categories') params.append('category', filters.category);
        if (filters.use_case && filters.use_case !== 'all-use-cases') params.append('use_case', filters.use_case);
        if (filters.listing_type && filters.listing_type !== 'all-types') params.append('listing_type', filters.listing_type);
        if (filters.pricing_tier && filters.pricing_tier !== 'all-tiers') params.append('pricing_tier', filters.pricing_tier);
        if (filters.search) params.append('search', filters.search);
        if (filters.max_price) params.append('max_price', filters.max_price);

        const response = await fetch(`${API_BASE}/api/marketplace/listings?${params.toString()}`);
        
        if (response.ok) {
          const result = await response.json();
          setListings(result.listings || []);
        }
      } catch (error) {
        console.error('Fetch listings error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchListings();
  }, [filters]);

  const resetFilters = () => {
    setFilters({
      category: '',
      use_case: '',
      listing_type: '',
      pricing_tier: '',
      search: '',
      max_price: ''
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Main Navigation */}
      <MainNavigation 
        onNavigate={(page) => {
          if (page === 'home') navigate('/');
          else if (page === 'services') navigate('/services');
          else if (page === 'products') navigate('/products');
          else if (page === 'marketplace') navigate('/marketplace');
          else if (page === 'about') navigate('/about');
          else if (page === 'contact') navigate('/contact');
          else if (page === 'cart') navigate('/cart');
          else if (page === 'profile') navigate('/profile');
        }} 
        currentPage="marketplace" 
        cartCount={0}
      />

      {/* Hero Banner - Modern Minimalistic Design */}
      <div className="relative bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-14 md:py-18">
            {/* Title with subtle accent */}
            <div className="max-w-4xl">
              <div className="inline-block mb-4">
                <span className="text-sm font-semibold text-[#0007b5] tracking-wider uppercase">3D CAD Library</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-medium text-slate-900 mb-6 page-title-uncage leading-tight">
                Marketplace
              </h1>
              <p className="text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">
                Discover, download, or print professional 3D designs for business or personal use
              </p>
              {!user?.is_designer && (
                <div className="mt-6">
                  <Button onClick={() => navigate('/designer/apply')} className="bg-[#0007b5] hover:bg-[#000690] text-white">
                    Become a Designer
                  </Button>
                </div>
              )}
            </div>
            
            {/* Decorative element - subtle accent line */}
            <div className="mt-8 w-24 h-1 bg-gradient-to-r from-[#0007b5] to-blue-400"></div>
          </div>
        </div>
        
        {/* Background decorative elements */}
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-5 pointer-events-none">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#0007b5] rounded-full blur-3xl"></div>
        </div>
      </div>

      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Filters */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="search">Search</Label>
                <Input
                  id="search"
                  type="text"
                  placeholder="Search designs..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({...prev, search: e.target.value}))}
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="category">Category</Label>
                <Select 
                  value={filters.category} 
                  onValueChange={(value) => setFilters(prev => ({...prev, category: value}))}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-categories">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="use_case">Use Case</Label>
                <Select 
                  value={filters.use_case} 
                  onValueChange={(value) => setFilters(prev => ({...prev, use_case: value}))}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="All use cases" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-use-cases">All Use Cases</SelectItem>
                    {useCases.map(useCase => (
                      <SelectItem key={useCase} value={useCase}>{useCase}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="listing_type">Type</Label>
                <Select 
                  value={filters.listing_type} 
                  onValueChange={(value) => setFilters(prev => ({...prev, listing_type: value}))}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-types">All Types</SelectItem>
                    <SelectItem value="individual">Individual Parts</SelectItem>
                    <SelectItem value="assembly">Assemblies</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="pricing_tier">Pricing</Label>
                <Select 
                  value={filters.pricing_tier} 
                  onValueChange={(value) => setFilters(prev => ({...prev, pricing_tier: value}))}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="All pricing" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-tiers">All Pricing</SelectItem>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="max_price">Max Price</Label>
                <Input
                  id="max_price"
                  type="number"
                  placeholder="$50"
                  value={filters.max_price}
                  onChange={(e) => setFilters(prev => ({...prev, max_price: e.target.value}))}
                  className="mt-2"
                />
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <Button onClick={resetFilters} variant="outline" size="sm">
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Listings Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0007b5] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading marketplace...</p>
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No designs found</h3>
            <p className="text-gray-600 mb-4">Try adjusting your filters or check back later for new designs.</p>
            {!user?.is_designer && (
              <Button onClick={() => navigate('/designer/apply')}>
                Become a Designer
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {listings.map((listing) => (
              <Card key={listing.id} className="hover:shadow-xl transition-all duration-300 cursor-pointer group">
                <div onClick={() => navigate(`/marketplace/listing/${listing.id}`)}>
                  <CardContent className="p-0">
                    <div className="aspect-square bg-gradient-to-br from-blue-100 to-purple-100 rounded-t-lg flex items-center justify-center group-hover:from-blue-200 group-hover:to-purple-200 transition-colors relative overflow-hidden">
                      {listing.preview_images && listing.preview_images.length > 0 ? (
                        <img 
                          src={listing.preview_images[0]}
                          alt={listing.title}
                          className="w-full h-full object-cover"
                        />
                      ) : listing.has_3d_preview ? (
                        <div className="flex flex-col items-center justify-center text-[#0007b5]">
                          <Package className="w-16 h-16 mb-2" />
                          <span className="text-xs font-medium bg-blue-100 px-2 py-1 rounded">3D Model</span>
                        </div>
                      ) : (
                        <Package className="w-16 h-16 text-blue-500" />
                      )}
                      
                      {/* Badges */}
                      <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
                        <div className="flex gap-1">
                          {listing.has_3d_preview && (
                            <Badge className="bg-[#0007b5] text-white text-xs">3D</Badge>
                          )}
                          {listing.listing_type === 'assembly' && (
                            <Badge className="bg-purple-600 text-white text-xs">Assembly</Badge>
                          )}
                        </div>
                        {listing.pricing_tier === 'free' && (
                          <Badge className="bg-green-600 text-white text-xs font-semibold">FREE</Badge>
                        )}
                        {listing.pricing_tier === 'premium' && (
                          <Badge className="bg-amber-600 text-white text-xs font-semibold">PREMIUM</Badge>
                        )}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-[#0007b5] transition-colors">{listing.title}</h3>
                      <p className="text-sm text-gray-600 mb-2">by {listing.designer_display_name || listing.designer_name}</p>
                      <p className="text-xs text-gray-500 mb-3 line-clamp-2">{listing.description}</p>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Personal:</span>
                          <span className="font-semibold text-green-600">{formatPrice(listing.personal_price)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Commercial:</span>
                          <span className="font-semibold text-[#0007b5]">{formatPrice(listing.commercial_price)}</span>
                        </div>
                      </div>
                      
                      <div className="mt-3 pt-3 border-t flex items-center justify-between">
                        <Badge variant="secondary" className="text-xs">
                          {listing.category}
                        </Badge>
                        <div className="flex items-center space-x-2 text-xs text-gray-500">
                          <Upload className="w-3 h-3" />
                          <span>{listing.download_count}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};


export const ListingDetailRoute = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getIdToken } = useAuth();
  const { formatPrice } = useCurrency();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchaseType, setPurchaseType] = useState('download');
  const [licenseType, setLicenseType] = useState('Personal Use');
  const [purchasing, setPurchasing] = useState(false);
  
  // Print configuration state
  const [printConfig, setPrintConfig] = useState({
    material: 'PLA',
    quality: 'Prototype',  // Valid new option
    strength: 'Cosmetic Only',
    smoothing: 'None - As Printed',
    color: 'Any',
    quantity: 1,
    country_code: 'US',  // Default to US
    postal_code: ''  // Optional postal code
  });
  const [printCost, setPrintCost] = useState(null);
  const [calculatingCost, setCalculatingCost] = useState(false);

  useEffect(() => {
    const fetchListing = async () => {
      try {
        // Use proper backend URL from environment variable  
        const API_BASE = BACKEND_URL;
        const response = await fetch(`${API_BASE}/api/marketplace/listings/${id}`);
        
        if (response.ok) {
          const result = await response.json();
          setListing(result);
          
          // Track design view in Google Analytics
          trackDesignView({
            id: result.id,
            title: result.title,
            category: result.category,
            personal_price: result.personal_price
          });
        } else {
          navigate('/marketplace');
        }
      } catch (error) {
        console.error('Fetch listing error:', error);
        navigate('/marketplace');
      } finally {
        setLoading(false);
      }
    };

    fetchListing();
  }, [id, navigate]);

  // Calculate print cost when configuration changes
  useEffect(() => {
    const calculatePrintCost = async () => {
      if (!listing || !listing.is_printable || purchaseType !== 'print') {
        setPrintCost(null);
        return;
      }

      setCalculatingCost(true);
      try {
        // Use proper backend URL from environment variable
        const API_BASE = BACKEND_URL;
        const params = new URLSearchParams({
          listing_id: id,
          material: printConfig.material,
          quality: printConfig.quality,
          strength: printConfig.strength,
          smoothing: printConfig.smoothing,
          color: printConfig.color,
          quantity: printConfig.quantity.toString(),
          license_type: licenseType === 'Personal Use' ? 'Personal' : 'Commercial',
          country_code: printConfig.country_code,
          postal_code: printConfig.postal_code || ''
        });
        
        const response = await fetch(`${API_BASE}/api/marketplace/calculate-print-cost?${params.toString()}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const result = await response.json();
          setPrintCost(result);
        } else {
          console.error('Failed to calculate print cost');
          setPrintCost(null);
        }
      } catch (error) {
        console.error('Print cost calculation error:', error);
        setPrintCost(null);
      } finally {
        setCalculatingCost(false);
      }
    };

    calculatePrintCost();
  }, [listing, purchaseType, printConfig, id, licenseType]);

  const handlePurchase = async () => {
    if (!user) {
      alert('Please sign in to make a purchase');
      return;
    }

    setPurchasing(true);
    try {
      // Use proper backend URL from environment variable
      const API_BASE = BACKEND_URL;
      const token = await getIdToken();

      let purchaseData = {
        listing_id: id,
        license_type: licenseType,
        purchase_type: purchaseType
      };

      // Add print configuration for print orders
      if (purchaseType === 'print') {
        purchaseData = {
          ...purchaseData,
          material: printConfig.material,
          quality: printConfig.quality,
          strength: printConfig.strength,
          smoothing: printConfig.smoothing,
          color: printConfig.color,
          quantity: printConfig.quantity
        };
      }

      // Use different endpoints for download vs print orders
      const endpoint = purchaseType === 'print' ? '/api/marketplace/print-order' : '/api/marketplace/purchases';
      
      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(purchaseData)
      });

      const result = await response.json();

      if (response.ok) {
        if (purchaseType === 'download') {
          alert('Purchase successful! Your download will be available shortly.');
          // Here we would typically redirect to a download page or start the download
        } else {
          alert(`Print order successful! Production time: ${result.production_time}. Delivery: ${result.delivery_weeks} weeks.`);
          // Here we would typically redirect to order tracking page
        }
      } else {
        throw new Error(result.detail || 'Purchase failed');
      }
    } catch (error) {
      alert(error.message || 'Purchase failed. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0007b5] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading design...</p>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="text-center p-6">
            <h2 className="text-xl font-semibold mb-4">Design Not Found</h2>
            <Button onClick={() => navigate('/marketplace')}>Back to Marketplace</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate current license price based on license type
  const getCurrentPrice = () => {
    return licenseType === 'Personal Use' ? listing.personal_price : listing.commercial_price;
  };
  
  const currentPrice = getCurrentPrice();

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-center relative">
            <Button 
              onClick={() => navigate('/marketplace')} 
              variant="outline"
              className="absolute left-0"
            >
              ← Back to Marketplace
            </Button>
            <button onClick={() => navigate('/')} className="flex items-center">
              <img 
                src="/logo-nav.png" 
                alt="Create 3D Works Logo" 
                className="h-10 sm:h-12 w-auto object-contain"
                style={{ minWidth: '120px' }}
              />
            </button>
            <button
              onClick={() => navigate('/cart')}
              className="absolute right-0 flex items-center space-x-2 font-medium text-slate-600 hover:text-[#0007b5] transition-colors"
            >
              <ShoppingCart className="w-5 h-5" />
              <span className="hidden sm:inline">Cart</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Design Preview */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-0">
                {/* 3D Preview or Image Gallery */}
                {listing.listing_type === 'assembly' ? (
                  // Assembly Preview
                  <div className="rounded-t-lg overflow-hidden">
                    <div className="bg-slate-50 p-4 border-b">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-slate-900">Assembly Preview</h3>
                        <Badge className="bg-purple-100 text-purple-800">Assembly ({listing.assembly_components?.length || 0} Parts)</Badge>
                      </div>
                    </div>
                    {listing.assembly_preview_type === 'image' && listing.assembly_preview_url ? (
                      <img 
                        src={`${BACKEND_URL}${listing.assembly_preview_url}`}
                        alt={`${listing.title} Assembly`}
                        className="w-full h-full object-cover"
                      />
                    ) : listing.assembly_preview_type === 'cad_file' && listing.assembly_preview_url ? (
                      <ThreeDPreview 
                        file={null}
                        cadFileUrl={`${BACKEND_URL}${listing.assembly_preview_url}`}
                        className="w-full"
                        onAnalysis={(data) => console.log('Assembly 3D analysis:', data)}
                      />
                    ) : (
                      <div className="aspect-square bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
                        <Package className="w-32 h-32 text-purple-500" />
                      </div>
                    )}
                  </div>
                ) : (
                  // Individual Part Preview
                  listing.cad_file_url && (listing.has_3d_preview !== false) ? (
                    <div className="rounded-t-lg overflow-hidden">
                      <div className="bg-slate-50 p-4 border-b">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-slate-900">Interactive 3D Preview</h3>
                          <Badge className="bg-blue-100 text-blue-800">3D Model</Badge>
                        </div>
                      </div>
                      <ThreeDPreview 
                        file={null}
                        cadFileUrl={`${BACKEND_URL}/api/${listing.cad_file_url}`}
                        className="w-full"
                        onAnalysis={(data) => console.log('Marketplace 3D analysis:', data)}
                      />
                    </div>
                  ) : (
                    <div className="aspect-square bg-gradient-to-br from-blue-100 to-purple-100 rounded-t-lg flex items-center justify-center">
                      {listing.preview_images && listing.preview_images.length > 0 ? (
                        <img 
                          src={listing.preview_images[0]} 
                          alt={listing.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Package className="w-32 h-32 text-blue-500" />
                      )}
                    </div>
                  )
                )}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900 mb-2">{listing.title}</h1>
                      <p className="text-gray-600">
                        by{' '}
                        {listing.creator_username ? (
                          <button
                            onClick={() => navigate(`/creator/${listing.creator_username}`)}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          >
                            {listing.designer_display_name || listing.designer_name}
                          </button>
                        ) : (
                          <span className="font-medium">{listing.designer_display_name || listing.designer_name}</span>
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <Upload className="w-4 h-4" />
                        <span>{listing.download_count} downloads</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                      <p className="text-gray-700 leading-relaxed">{listing.description}</p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <Label className="text-sm text-gray-600">Category</Label>
                        <p className="font-medium">{listing.category}</p>
                      </div>
                      <div>
                        <Label className="text-sm text-gray-600">Use Case</Label>
                        <p className="font-medium">{listing.use_case}</p>
                      </div>
                      {listing.dimensions && (
                        <div>
                          <Label className="text-sm text-gray-600">Dimensions</Label>
                          <p className="font-medium">{listing.dimensions}</p>
                        </div>
                      )}
                      <div>
                        <Label className="text-sm text-gray-600">File Format</Label>
                        <p className="font-medium">
                          {listing.listing_type === 'assembly' && listing.assembly_components ? 
                            (() => {
                              const formats = [...new Set(listing.assembly_components.map(c => c.file_format))];
                              return formats.join(', ');
                            })()
                            : (listing.file_format || 'STL')
                          }
                        </p>
                      </div>
                    </div>

                    {listing.tags && listing.tags.length > 0 && (
                      <div>
                        <Label className="text-sm text-gray-600 mb-2 block">Tags</Label>
                        <div className="flex flex-wrap gap-2">
                          {listing.tags.map((tag, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Assembly Parts List - OUTSIDE space-y-4 container for proper spacing */}
                  {listing.listing_type === 'assembly' && listing.show_component_names && listing.assembly_components && (
                    <div className="mt-16 mb-6">
                      <h3 className="font-semibold text-gray-900 mb-2">Parts List ({listing.assembly_components.length} parts)</h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <ul className="space-y-2">
                          {listing.assembly_components.map((comp, index) => (
                            <li key={comp.id} className="flex items-center text-sm">
                              <span className="font-mono text-gray-500 mr-2">{index + 1}.</span>
                              <span className="font-medium">{comp.display_name}</span>
                              <span className="text-gray-500 ml-2 text-xs">({comp.file_format})</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Purchase Panel */}
          <div>
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Get This Design</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Purchase Type</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Button
                      variant={purchaseType === 'download' ? 'default' : 'outline'}
                      onClick={() => setPurchaseType('download')}
                      className="text-sm"
                    >
                      Download
                    </Button>
                    <Button
                      variant={purchaseType === 'print' ? 'default' : 'outline'}
                      onClick={() => setPurchaseType('print')}
                      className="text-sm"
                    >
                      Print Order
                    </Button>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium">License Type</Label>
                  <Select value={licenseType} onValueChange={setLicenseType}>
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Personal Use">Personal Use</SelectItem>
                      <SelectItem value="Commercial Use">Commercial Use</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Print Configuration Options */}
                {purchaseType === 'print' && listing.is_printable && (
                  <div className="space-y-4 border-t pt-4">
                    <h4 className="font-medium text-sm">Print Configuration</h4>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-medium">Material</Label>
                        <Select 
                          value={printConfig.material} 
                          onValueChange={(value) => setPrintConfig(prev => ({...prev, material: value}))}
                        >
                          <SelectTrigger className="text-sm mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="PLA">PLA - Standard</SelectItem>
                            <SelectItem value="PLA+">PLA+ - Reinforced</SelectItem>
                            <SelectItem value="Silk PLA">Silk PLA - Glossy finish</SelectItem>
                            <SelectItem value="Wood-fill PLA">Wood-fill PLA - Reinforced, Wood-like finish</SelectItem>
                            <SelectItem value="Metal-fill PLA">Metal-fill PLA - Reinforced, Metal-like finish</SelectItem>
                            <SelectItem value="CF PLA">CF PLA - Carbon Fiber reinforced</SelectItem>
                            <SelectItem value="HT PLA">HT PLA - High Temp PLA</SelectItem>
                            <SelectItem value="PETG">PETG</SelectItem>
                            <SelectItem value="CPE+">CPE+</SelectItem>
                            <SelectItem value="ABS">ABS</SelectItem>
                            <SelectItem value="ABS+">ABS+</SelectItem>
                            <SelectItem value="ASA">ASA</SelectItem>
                            <SelectItem value="TPU">TPU</SelectItem>
                            <SelectItem value="TPU 85A">TPU 85A</SelectItem>
                            <SelectItem value="TPU 64D">TPU 64D (semi-rigid)</SelectItem>
                            <SelectItem value="PC">PC - coming soon!</SelectItem>
                            <SelectItem value="PC-CF">PC-CF - coming soon!</SelectItem>
                            <SelectItem value="PA">PA - coming soon!</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs font-medium">Quality</Label>
                        <Select 
                          value={printConfig.quality} 
                          onValueChange={(value) => setPrintConfig(prev => ({...prev, quality: value}))}
                        >
                          <SelectTrigger className="text-sm mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Concept">Concept</SelectItem>
                            <SelectItem value="Prototype">Prototype</SelectItem>
                            <SelectItem value="Test/Demo">Test/Demo</SelectItem>
                            <SelectItem value="End Use">End Use</SelectItem>
                            <SelectItem value="Display">Display</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs font-medium">Strength</Label>
                        <Select 
                          value={printConfig.strength} 
                          onValueChange={(value) => setPrintConfig(prev => ({...prev, strength: value}))}
                        >
                          <SelectTrigger className="text-sm mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Cosmetic Only">Cosmetic Only</SelectItem>
                            <SelectItem value="Reinforced">Reinforced</SelectItem>
                            <SelectItem value="Functional">Functional</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs font-medium">Smoothing</Label>
                        <Select 
                          value={printConfig.smoothing} 
                          onValueChange={(value) => setPrintConfig(prev => ({...prev, smoothing: value}))}
                        >
                          <SelectTrigger className="text-sm mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="None - As Printed">None - As Printed</SelectItem>
                            <SelectItem value="Light Sanding">Light Sanding</SelectItem>
                            <SelectItem value="Smooth Finish">Smooth Finish</SelectItem>
                            <SelectItem value="Premium Finish">Premium Finish</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-xs font-medium">Color</Label>
                        <Input
                          value={printConfig.color}
                          onChange={(e) => setPrintConfig(prev => ({...prev, color: e.target.value}))}
                          placeholder="Any"
                          className="text-sm mt-1"
                        />
                      </div>

                      <div>
                        <Label className="text-xs font-medium">Quantity</Label>
                        <Input
                          type="number"
                          min="1"
                          max="10"
                          value={printConfig.quantity}
                          onChange={(e) => setPrintConfig(prev => ({...prev, quantity: parseInt(e.target.value) || 1}))}
                          className="text-sm mt-1"
                        />
                      </div>
                    </div>

                    {/* Shipping Destination */}
                    <div className="border-t pt-3 mt-3">
                      <h5 className="font-medium text-xs mb-3 text-gray-700">Shipping Destination</h5>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs font-medium">Country</Label>
                          <Select 
                            value={printConfig.country_code} 
                            onValueChange={(value) => setPrintConfig(prev => ({...prev, country_code: value}))}
                          >
                            <SelectTrigger className="text-sm mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="US">United States</SelectItem>
                              <SelectItem value="CA">Canada</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label className="text-xs font-medium">Postal/Zip Code (Optional)</Label>
                          <Input
                            value={printConfig.postal_code}
                            onChange={(e) => setPrintConfig(prev => ({...prev, postal_code: e.target.value}))}
                            placeholder="Optional"
                            className="text-sm mt-1"
                          />
                        </div>
                      </div>
                      <p className="text-[10px] text-gray-500 mt-1">
                        Shipping calculated from Dartmouth, NS, Canada. Postal/zip code helps provide more accurate shipping estimates.
                      </p>
                    </div>
                  </div>
                )}

                <div className="bg-gray-50 rounded-lg p-4">
                  {purchaseType === 'download' ? (
                    <>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">Download License Price</span>
                        <span className="text-2xl font-bold text-green-600">
                          {formatPrice(currentPrice)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 space-y-1 mb-2">
                        <p>• Instant download after purchase</p>
                        <p>• {licenseType} license included</p>
                        <p>• Unlimited downloads</p>
                      </div>
                    </>
                  ) : (
                    <>
                      {calculatingCost ? (
                        <div className="text-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0007b5] mx-auto mb-2"></div>
                          <span className="text-sm text-gray-600">Calculating print cost...</span>
                        </div>
                      ) : printCost ? (
                        <div className="space-y-3">
                          <div className="flex justify-between items-center mb-3 pb-3 border-b-2 border-gray-300">
                            <span className="text-sm font-semibold text-gray-700">Total Order Cost</span>
                            <span className="text-2xl font-bold text-green-600">
                              {formatPrice(printCost.total_cost)}
                            </span>
                          </div>
                          
                          {/* License Cost Section */}
                          <div className="space-y-1">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-700">License Fee ({licenseType})</span>
                              <span className="text-lg font-semibold text-gray-900">{formatPrice(printCost.license_fee)}</span>
                            </div>
                          </div>

                          {/* Print Order Section */}
                          <div className="space-y-1 pt-2 border-t">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-700">Print Cost (Instant Quote)</span>
                              <span className="text-lg font-semibold text-gray-900">{formatPrice(printCost.print_cost)}</span>
                            </div>
                            
                            <div className="pl-3 space-y-0.5 text-[10px] text-gray-500">
                              <div className="flex justify-between">
                                <span>Quantity:</span>
                                <span>{printConfig.quantity}x @ {formatPrice(printCost.cost_per_item)} each</span>
                              </div>
                              <div className="flex justify-between font-medium text-gray-700 pt-1 border-t">
                                <span>Total Order Cost:</span>
                                <span>{formatPrice(printCost.total_cost)}</span>
                              </div>
                            </div>
                          </div>

                          {/* Production Details */}
                          <div className="text-xs text-gray-600 space-y-1 pt-2 border-t bg-blue-50 rounded px-2 py-2 -mx-1">
                            {printCost.is_assembly && printCost.component_count && (
                              <div className="flex justify-between">
                                <span>📦 Components in assembly:</span>
                                <span className="font-medium">{printCost.component_count} parts</span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span>⏱️ Production time:</span>
                              <span className="font-medium">{printCost.production_time}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>🚚 Delivery estimate:</span>
                              <span className="font-medium">{printCost.delivery_weeks} weeks</span>
                            </div>
                          </div>
                        </div>
                      ) : listing.is_printable ? (
                        <div className="text-center py-4">
                          <span className="text-sm text-gray-600">Configure print options to see pricing</span>
                        </div>
                      ) : (
                        <div className="text-center py-4 text-red-600">
                          <span className="text-sm">This design is not available for printing</span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <Button 
                  onClick={handlePurchase}
                  disabled={purchasing || !user || (purchaseType === 'print' && (!printCost || calculatingCost))}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-lg font-semibold py-3"
                >
                  {purchasing ? 'Processing...' : 
                   calculatingCost ? 'Calculating...' :
                   purchaseType === 'download' ? `Buy & Download - ${formatPrice(currentPrice)}` : 
                   `Order Print - ${printCost ? formatPrice(parseFloat(currentPrice) + parseFloat(printCost.total_cost)) : formatPrice(0)}`}
                </Button>

                {!user && (
                  <p className="text-xs text-gray-500 text-center">
                    Please sign in to make a purchase
                  </p>
                )}

                <div className="pt-4 border-t">
                  <div className="space-y-2 text-xs text-gray-500">
                    <p>✓ Secure payment processing</p>
                    <p>✓ Instant access after payment</p>
                    <p>✓ 30-day satisfaction guarantee</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};


export const DesignerEditRoute = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getIdToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [listing, setListing] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    use_case: '',
    personal_price: '',
    commercial_price: '',
    is_printable: true,
    tags: '',
    dimensions: '',
    dimensions_unit: 'mm',
    dimensions_unit_custom: '',
    complexity_level: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const categories = [
    'Automotive', 'Home & Living', 'Garden & Outdoor', 'Organization', 
    'Electronics', 'Toys & Games', 'Art & Decorative', 'Tools & Hardware',
    'Fashion & Jewelry', 'Replacement Parts', 'Sim Racing', 'Flight Simulator', 'Other'
  ];

  const useCases = ['Functional', 'Decorative', 'Replacement', 'Educational', 'Prototype'];

  // Load listing data
  useEffect(() => {
    const fetchListing = async () => {
      if (!user || !id) return;

      try {
        const API_BASE = BACKEND_URL;
        const token = await getIdToken();
        
        const response = await fetch(`${API_BASE}/api/marketplace/listings/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const listingData = await response.json();
          
          // Verify this listing belongs to the current user
          if (listingData.designer_id !== user.id) {
            alert('You can only edit your own listings');
            navigate('/designer/dashboard');
            return;
          }

          setListing(listingData);
          setFormData({
            title: listingData.title || '',
            description: listingData.description || '',
            category: listingData.category || '',
            use_case: listingData.use_case || '',
            personal_price: listingData.personal_price?.toString() || '',
            commercial_price: listingData.commercial_price?.toString() || '',
            is_printable: listingData.is_printable || true,
            tags: (listingData.tags || []).join(', '),
            dimensions: listingData.dimensions || '',
            dimensions_unit: listingData.dimensions_unit || 'mm',
            dimensions_unit_custom: listingData.dimensions_unit_custom || '',
            complexity_level: listingData.complexity_level || ''
          });
        } else {
          throw new Error('Failed to fetch listing');
        }
      } catch (error) {
        console.error('Error fetching listing:', error);
        alert('Failed to load listing for editing');
        navigate('/designer/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchListing();
  }, [user, id, navigate, getIdToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const API_BASE = BACKEND_URL;
      const token = await getIdToken();

      const updateData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        use_case: formData.use_case,
        personal_price: parseFloat(formData.personal_price),
        commercial_price: parseFloat(formData.commercial_price),
        is_printable: formData.is_printable,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        dimensions: formData.dimensions,
        dimensions_unit: formData.dimensions_unit === 'other' ? formData.dimensions_unit_custom : formData.dimensions_unit,
        complexity_level: formData.complexity_level
      };

      const response = await fetch(`${API_BASE}/api/marketplace/listings/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: 'Listing updated successfully!'
        });
        setTimeout(() => navigate('/designer/dashboard'), 2000);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Update failed');
      }
    } catch (error) {
      console.error('Error updating listing:', error);
      setSubmitStatus({
        type: 'error',
        message: error.message || 'Failed to update listing'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <SharedPageLayout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Required</h2>
          <p className="text-gray-600">Please sign in to edit listings.</p>
        </div>
      </SharedPageLayout>
    );
  }

  if (loading) {
    return (
      <SharedPageLayout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0007b5] mx-auto mb-4"></div>
          <p>Loading listing...</p>
        </div>
      </SharedPageLayout>
    );
  }

  if (!listing) {
    return (
      <SharedPageLayout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Listing Not Found</h2>
          <Button onClick={() => navigate('/designer/dashboard')}>Back to Dashboard</Button>
        </div>
      </SharedPageLayout>
    );
  }

  return (
    <SharedPageLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <header className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-8 h-8 bg-gradient-to-br from-[#0007b5] to-[#0007b5] rounded-lg flex items-center justify-center">
                <Edit className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Edit Design</h1>
            </div>
            <Button onClick={() => navigate('/designer/dashboard')} variant="outline">
              Back to Dashboard
            </Button>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Design Information</CardTitle>
              <CardDescription>Update your design details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title">Design Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                    required
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData(prev => ({...prev, category: value}))}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                  required
                  className="mt-2"
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="use_case">Use Case *</Label>
                  <Select 
                    value={formData.use_case} 
                    onValueChange={(value) => setFormData(prev => ({...prev, use_case: value}))}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select use case" />
                    </SelectTrigger>
                    <SelectContent>
                      {useCases.map(useCase => (
                        <SelectItem key={useCase} value={useCase}>{useCase}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="complexity">Complexity Level</Label>
                  <Input
                    id="complexity"
                    value={formData.complexity_level}
                    onChange={(e) => setFormData(prev => ({...prev, complexity_level: e.target.value}))}
                    placeholder="e.g., Beginner, Intermediate, Advanced"
                    className="mt-2"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card>
            <CardHeader>
              <CardTitle>Pricing</CardTitle>
              <CardDescription>Set your download prices</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="personal_price">Personal Use Price ($) *</Label>
                  <Input
                    id="personal_price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.personal_price}
                    onChange={(e) => setFormData(prev => ({...prev, personal_price: e.target.value}))}
                    required
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="commercial_price">Commercial Use Price ($) *</Label>
                  <Input
                    id="commercial_price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.commercial_price}
                    onChange={(e) => setFormData(prev => ({...prev, commercial_price: e.target.value}))}
                    required
                    className="mt-2"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Additional Details */}
          <Card>
            <CardHeader>
              <CardTitle>Additional Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tags">Tags (comma separated)</Label>
                  <Input
                    id="tags"
                    value={formData.tags}
                    onChange={(e) => setFormData(prev => ({...prev, tags: e.target.value}))}
                    placeholder="e.g., mechanical, automotive, functional"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="dimensions">Dimensions</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      id="dimensions"
                      type="text"
                      placeholder="150 x 100 x 50"
                      value={formData.dimensions}
                      onChange={(e) => setFormData(prev => ({...prev, dimensions: e.target.value}))}
                      className="flex-1"
                    />
                    <Select 
                      value={formData.dimensions_unit} 
                      onValueChange={(value) => setFormData(prev => ({...prev, dimensions_unit: value}))}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mm">mm</SelectItem>
                        <SelectItem value="cm">cm</SelectItem>
                        <SelectItem value="in">in</SelectItem>
                        <SelectItem value="ft">ft</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.dimensions_unit === 'other' && (
                    <Input
                      type="text"
                      placeholder="Enter custom unit"
                      value={formData.dimensions_unit_custom}
                      onChange={(e) => setFormData(prev => ({...prev, dimensions_unit_custom: e.target.value}))}
                      className="mt-2"
                    />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit Status */}
          {submitStatus && (
            <div className={`rounded-lg p-4 ${
              submitStatus.type === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {submitStatus.message}
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/designer/dashboard')}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="bg-[#0007b5] hover:bg-[#000690]"
            >
              {isSubmitting ? 'Updating...' : 'Update Listing'}
            </Button>
          </div>
        </form>
      </div>
    </SharedPageLayout>
  );
};
